import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './EventPage.module.css'

export function EventPage() {
  const navigate = useNavigate()
  const [openDressCode, setOpenDressCode] = useState<string | null>(null)

  useEffect(() => {
    const originalTitle = document.title
    document.title = 'The Winter Convocation | SRoy'
    return () => {
      document.title = originalTitle
    }
  }, [])

  const handleBack = () => {
    navigate('/')
  }

  const toggleDressCode = (code: string) => {
    setOpenDressCode(openDressCode === code ? null : code)
  }

  const dressCodeExplanations: Record<string, { men: string; women: string }> = {
    'Black Tie': {
      men: 'Black Tie (dinner jacket or tuxedo): black dinner jacket with satin lapels, matching trousers, white dress shirt, black bow tie, and black formal shoes.',
      women: 'Floor-length evening dress, a formal cocktail dress, or an elegant ensemble of comparable formality. Gloves and refined accessories are welcome but not required.'
    },
    'White Tie': {
      men: 'White Tie: black evening tailcoat, matching trousers, white waistcoat, white bow tie, formal white shirt, and black patent or highly polished dress shoes.',
      women: 'Full-length evening dress of the highest formality. Traditional ball gowns, structured silhouettes, and elevated fabrics are particularly appropriate; refined jewellery and evening accessories are encouraged.'
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.snowflakes}>
        {Array.from({ length: 50 }).map((_, i) => (
          <div key={i} className={styles.snowflake} style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${10 + Math.random() * 10}s`
          }}>❄</div>
        ))}
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.venueGraphic}>
            <div className={styles.venuePlaceholder}>Venue Sketch Placeholder</div>
          </div>
          <div className={styles.ornamentTop}></div>
          <h1 className={styles.title}>
            The Winter Convocation
            <span className={styles.occurrence}>VI</span>
          </h1>
          <p className={styles.mottoHeader}>Ad Mensam, ad Vina, ad Noctem</p>
          <div className={styles.ornamentBottom}></div>
        </div>
        <div className={styles.eventContent}>
          <div className={styles.invitationCard}>
            <div className={styles.invitationText}>
              <p className={styles.invitationLine}>
                Convened at{' '}
                <a
                  href="https://www.thepennestate.co.uk/the-estate/the-penn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.venueLink}
                >
                  Pennsylvania Castle
                </a>
              </p>
              <p className={styles.invitationLine}>by Scarlet & Steven,</p>
              <p className={styles.invitationLine}>who request the pleasure of your company.</p>
            </div>
            <div className={styles.dateRange}>
              <p className={styles.dateText}>Friday 27th November until Sunday 29th November</p>
            </div>
          </div>

          <div className={styles.accommodationBlock}>
            <h3 className={styles.accommodationTitle}>Accommodation & Facilities</h3>
            <div className={styles.accommodationContent}>
              <p className={styles.description}>
                We’ll have the castle to ourselves for the weekend. Everyone will stay within the castle itself — proper en-suite bedrooms, a series of generous rooms to wander between, and large dining spaces where evenings are likely to stretch. There’s a heated indoor pool, broad stone terraces and gardens above the sea, and no shortage of indoor and outdoor corners to slip away to once the wine takes hold, the fire is lit, or the morning arrives more gently than planned.
              </p>
            </div>
          </div>

          <div className={styles.dayBlock}>
            <h3 className={styles.dayTitle}>Convivium</h3>
            <p className={styles.dayDate}>Friday 27th November</p>
            <div className={styles.dressCodeContainer}>
              <div className={styles.dressCodeTiming}>
                <div className={styles.dressCodeColumn}>
                  <p className={styles.dressCodeTimingItem}>
                    <strong>Morning:</strong>
                  </p>
                  <p className={styles.dressCodeTimingValue}>No dress requirement</p>
                </div>
                <div className={styles.dressCodeColumn}>
                  <p className={styles.dressCodeTimingItem}>
                    <strong>Evening:</strong>
                  </p>
                  <button
                    className={styles.dressCode}
                    onClick={() => toggleDressCode('Black Tie')}
                    type="button"
                  >
                    Black Tie
                  </button>
                </div>
              </div>
              {openDressCode === 'Black Tie' && (
                <div className={styles.dressCodeExplanation}>
                  <p>
                    <strong>Men:</strong> {dressCodeExplanations['Black Tie'].men}
                  </p>
                  <p>
                    <strong>Women:</strong> {dressCodeExplanations['Black Tie'].women}
                  </p>
                  <p>
                    National dress is welcome on this evening.
                  </p>
                </div>
              )}
            </div>
            <div className={styles.dayContent}>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>Carriages</h4>
                <p className={styles.description}>
                  Carriages from London to the estate will be arranged, with guests travelling in small groups. Collection preferences will be gathered individually nearer the time, with timings and arrangements confirmed privately. Guests who prefer to make their own way to the estate are, of course, welcome to do so. We expect to depart London in the afternoon, arriving at the castle in good time before evening.
                </p>
              </div>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>Banquet</h4>
                <p className={styles.description}>
                  On Friday evening, following nightfall, guests will gather for a black tie banquet, with wine and drinks served throughout. The tone is festive and relaxed — an opening night intended to ease everyone into the rhythm of the weekend ahead.
                </p>
              </div>
            </div>
          </div>


          <div className={styles.dayBlock}>
            <h3 className={styles.dayTitle}>Convocatio Hiemalis</h3>
            <p className={styles.dayDate}>Saturday 28th November</p>
            <div className={styles.dressCodeContainer}>
              <div className={styles.dressCodeTiming}>
                <div className={styles.dressCodeColumn}>
                  <p className={styles.dressCodeTimingItem}>
                    <strong>Morning:</strong>
                  </p>
                  <p className={styles.dressCodeTimingValue}>No dress requirement</p>
                </div>
                <div className={styles.dressCodeColumn}>
                  <p className={styles.dressCodeTimingItem}>
                    <strong>Evening:</strong>
                  </p>
                  <button
                    className={styles.dressCode}
                    onClick={() => toggleDressCode('White Tie')}
                    type="button"
                  >
                    White Tie
                  </button>
                </div>
              </div>
              {openDressCode === 'White Tie' && (
                <div className={styles.dressCodeExplanation}>
                  <p>
                    <strong>Men:</strong> {dressCodeExplanations['White Tie'].men}
                  </p>
                  <p>
                    <strong>Women:</strong> {dressCodeExplanations['White Tie'].women}
                  </p>
                  <p>
                    National dress will not be worn on this evening.
                  </p>
                  <p className={styles.dressCodeNote}>
                    Orders and decorations may be worn.
                  </p>
                </div>
              )}
            </div>
            <div className={styles.dayContent}>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>Breakfast & Brunch</h4>
                <p className={styles.description}>
                  Breakfast and brunch will be available for guests to enjoy at their leisure throughout the morning.
                </p>
              </div>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>The Day</h4>
                <p className={styles.description}>
                  The day is left entirely open for guests to make full use of the castle and its grounds — time to swim, walk, read, recover, or simply linger.
                </p>
              </div>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>Dinner</h4>
                <p className={styles.description}>
                  Details of Saturday evening's dinner will follow. It will form the focal point of the weekend.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.dayBlock}>
            <h3 className={styles.dayTitle}>Dies Refectiōnis</h3>
            <p className={styles.dayDate}>Sunday 29th November</p>
            <div className={styles.dressCodeContainer}>
              <div className={styles.dressCodeTiming}>
                <div className={styles.dressCodeColumn}>
                  <p className={styles.dressCodeTimingItem}>
                    <strong>Morning:</strong>
                  </p>
                  <p className={styles.dressCodeTimingValue}>No dress requirement</p>
                </div>
                <div className={styles.dressCodeColumn}>
                  <p className={styles.dressCodeTimingItem}>
                    <strong>Evening:</strong>
                  </p>
                  <p className={styles.dressCodeTimingValue}>No dress requirement</p>
                </div>
              </div>
            </div>
            <div className={styles.dayContent}>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>Brunch</h4>
                <p className={styles.description}>
                  A light brunch will be available for guests to enjoy at their leisure throughout the late morning.
                </p>
              </div>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>Departure</h4>
                <p className={styles.description}>
                  Carriages will depart the castle in the mid-afternoon, returning guests to London. Those who prefer to make their own onward arrangements are welcome to do so.
                </p>
              </div>
              <div className={styles.daySection}>
                <h4 className={styles.daySectionTitle}>Extended Stay</h4>
                <p className={styles.description}>
                  For guests for whom it is more convenient, the estate will remain available on Sunday night, with departure by 10.00am on Monday morning.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.rsvpSection}>
            <h3 className={styles.rsvpTitle}>RSVP</h3>
            <p className={styles.rsvpText}>
              Given the scale and nature of the weekend, we ask that guests confirm their attendance by the date indicated below. Late changes to RSVPs have a significant impact on arrangements and costs, and we would be grateful if confirmations could be treated as final wherever possible.
            </p>
            <p className={styles.rsvpText}>
              Should circumstances genuinely change, please let us know as soon as you are able so that we can adjust accordingly.
            </p>
          </div>

          <button onClick={handleBack} className={styles.backButton}>
            Return
          </button>
        </div>
      </div>
    </div>
  )
}

